import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { YoolTable, StatusPill, StackedCell, MonoText, RowActions, IconButton } from '../../components/ui/YoolTable';
import { KpiGrid } from '../../components/ui/KpiCard';
import { RefreshCw, Plus, Clock, CheckCircle, FileText, DollarSign, Calendar } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { OrdenespagoService } from '../../services/ordenespagoService';
import { getValorUfVigente } from '../../services/ufDiariaService';
import { formatUF, formatCLP } from '../../styles/tokens/formatters';
import { useNavigate } from 'react-router-dom';
import { useSupabaseClient } from '../../lib/supabase';
import { textoBusquedaOrden, rutaContratacion } from '../../lib/ordenesPago/trazabilidad';
import { CeldaTrazabilidad, CodigoTraza } from '../../components/ordenes-pago/Trazabilidad';

const OrdenespagoList = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  // UF vigente para convertir los montos a pesos. null = no hay valor en
  // uf_diaria; en ese caso se muestra solo UF, sin inventar una tasa.
  const [uf, setUf] = useState(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  // Pagos y certificados tienen RLS que exige rol `authenticated`: con el cliente
  // anónimo la trazabilidad vuelve vacía aunque las filas existan.
  const supabaseClient = useSupabaseClient();

  const fetchUf = async () => {
    setUf(await getValorUfVigente());
  };

  const fetchOrdenes = useCallback(async () => {
    try {
      setLoading(true);

      const ordenesData = await OrdenespagoService.getOrdenesPagoConTrazabilidad({}, supabaseClient);

      // La búsqueda de YoolTable compara campos de primer nivel, así que los
      // códigos de la cadena se aplanan en una columna que sí puede recorrer.
      setOrdenes((ordenesData || []).map((o) => ({ ...o, _busqueda: textoBusquedaOrden(o) })));
    } catch (error) {
      console.error('Error fetching órdenes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las órdenes de pago",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabaseClient]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await OrdenespagoService.getEstadisticas(supabaseClient);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [supabaseClient]);

  useEffect(() => {
    if (!supabaseClient) return;
    fetchOrdenes();
    fetchStats();
    fetchUf();
  }, [supabaseClient, fetchOrdenes, fetchStats]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta orden de pago?')) {
      return;
    }

    try {
      await OrdenespagoService.deleteOrdenPago(id);
      toast({
        title: "Éxito",
        description: "Orden de pago eliminada correctamente"
      });
      fetchOrdenes();
      fetchStats();
    } catch (error) {
      console.error('Error deleting orden:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la orden de pago",
        variant: "destructive"
      });
    }
  };

  // ordenes_pago.monto está en UF (moneda canónica). El equivalente en pesos es
  // referencial: se calcula con la UF vigente, no es el monto que se cobró —
  // ese lo congela el cargo procesado (subscription_charges.valor_uf_cobro).
  const montoEnPesos = (montoUf) => {
    const valorUf = uf?.valorClp;
    if (!valorUf) return null;
    const numerico = parseFloat(montoUf);
    if (isNaN(numerico)) return null;
    return formatCLP(numerico * valorUf);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CL');
  };

  const columns = [
    {
      key: 'contrataciones',
      label: 'Cliente',
      render: (r) => (
        <StackedCell
          primary={r.contrataciones?.nombre || 'N/A'}
          secondary={r.contrataciones?.mail || 'N/A'}
        />
      ),
    },
    {
      // `contrataciones` no tiene `numero_contrato`: el identificador de la
      // contratación que generó la orden es su `codigo` (CT-…).
      key: 'contrato',
      label: 'Contrato',
      render: (r) =>
        r.contrataciones?.codigo ? (
          <CodigoTraza
            codigo={r.contrataciones.codigo}
            to={rutaContratacion(r.contrataciones.codigo)}
            titulo="Contratación"
          />
        ) : (
          <MonoText>N/A</MonoText>
        ),
    },
    {
      key: 'trazabilidad',
      label: 'Trazabilidad',
      render: (r) => <CeldaTrazabilidad orden={r} />,
    },
    {
      key: 'monto',
      label: 'Monto',
      numeric: true,
      sortable: true,
      sortValue: (r) => r.monto || 0,
      render: (r) => (
        <StackedCell
          primary={formatUF(r.monto)}
          secondary={montoEnPesos(r.monto) ?? 'UF no disponible'}
        />
      ),
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (r) => <StatusPill estado={r.estado} />,
    },
    {
      key: 'created_at',
      label: 'Fecha Creación',
      render: (r) => formatDate(r.created_at),
    },
    {
      key: 'fecha_expiracion',
      label: 'Fecha Expiración',
      render: (r) => formatDate(r.fecha_expiracion),
    },
    {
      key: 'acciones',
      label: '',
      align: 'right',
      render: (r) => (
        <RowActions>
          <IconButton icon="eye" title="Ver" onClick={() => navigate(`/ordenes-pago/${r.id}`)} />
          <IconButton icon="edit" title="Editar" onClick={() => navigate(`/ordenes-pago/${r.id}/editar`)} />
          <IconButton icon="trash" title="Eliminar" danger onClick={() => handleDelete(r.id)} />
        </RowActions>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Estadísticas */}
      {stats && (
        <KpiGrid
          cards={[
            {
              title: "Total Órdenes",
              value: stats.total,
              description: "Órdenes de pago registradas",
              icon: FileText,
              trend: null,
              color: "blue"
            },
            {
              title: "Pendientes",
              value: stats.pendientes,
              description: "Órdenes por pagar",
              icon: Clock,
              trend: null,
              color: "yellow"
            },
            {
              title: "Pagadas",
              value: stats.pagadas,
              description: "Órdenes completadas",
              icon: CheckCircle,
              trend: null,
              color: "green"
            },
            {
              title: "Monto Total",
              value: formatUF(stats.montoTotal),
              description: montoEnPesos(stats.montoTotal)
                ? `${montoEnPesos(stats.montoTotal)} al valor UF de hoy`
                : "Valor total de órdenes",
              icon: DollarSign,
              trend: null,
              color: "purple"
            },
            {
              title: "Monto Pagado",
              value: formatUF(stats.montoPagado),
              description: montoEnPesos(stats.montoPagado)
                ? `${montoEnPesos(stats.montoPagado)} al valor UF de hoy`
                : "Total recaudado",
              icon: DollarSign,
              trend: null,
              color: "green"
            },
            {
              title: "Tasa de Pago",
              value: `${stats.total > 0 ? ((stats.pagadas / stats.total) * 100).toFixed(1) : '0'}%`,
              description: "Porcentaje de órdenes pagadas",
              icon: Calendar,
              trend: null,
              color: "blue"
            }
          ]}
        />
      )}

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
          Cargando órdenes de pago...
        </div>
      ) : (
        <YoolTable
          columns={columns}
          rows={ordenes}
          rowKey="id"
          search={{ placeholder: 'Buscar por cliente, email o código (OP·CT·PY·SU·CE)…', keys: ['_busqueda'] }}
          filters={[{ key: 'estado', label: 'Estado', options: ['pendiente', 'procesando', 'pagado', 'expirado', 'cancelado'] }]}
          toolbar={
            <Button onClick={() => navigate('/ordenes-pago/nueva')}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva Orden
            </Button>
          }
        />
      )}
    </div>
  );
};

export default OrdenespagoList;